<!-- eslint-disable no-undef -->
<script setup>
  // Imports
  import { ref } from 'vue';
  import { TrashIcon } from '@heroicons/vue/24/solid';
  import LoadingAnimation3 from '../loadingIcons/LoadingAnimation3.vue';
  import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

  // Definitions
  const props = defineProps({
    availableValues: {
      type: Object,
      default: () => ({})
    }
  });

  const emit = defineEmits(['reportDetails', 'close']);

  const loading = ref(false);
  const name = ref('');
  const columns = ref(['']);
  const values = ref(['']);
  const formats = ref(['']);
  const columnOptions = ref(Object.keys(props.availableValues).sort((a, b) => a.localeCompare(b)));
  const formatOptions = ref([])
  const error = ref('');
  const includeCoordinates = ref(false);

  // Methods
  const handleValueChange = (event, index) => {
    const valueKey = event.target.value;
    let value = props.availableValues[valueKey];
    let valueType = typeof value;
    
    // Save the value key
    values.value[index] = valueKey;

    // Format options for string values.
    if (valueType === 'string') {
      // Check if the value is a stringified number.
      if (!isNaN(value) && value.trim() !== '') {
        value = Number(value);
        valueType = 'number';
      }
      // Date format (YYYY-MM-DD)
      else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        formatOptions.value[index] = [
          'DD-MM-YYYY',
          'DD/MM/YYYY',
          'MM-DD-YYYY',
          'MM/DD/YYYY',
          'YYYY-MM-DD',
          'YYYY/MM/DD'
        ];
        formats.value[index] = 'YYYY-MM-DD';
      }
      // Time format (HH:MM:SS or x Days HH:MM:SS)
      else if (/^\d{2}:\d{2}:\d{2}$/.test(value) || /^\d+ Days? \d{2}:\d{2}:\d{2}$/.test(value)) {
        formatOptions.value[index] = [
          'days HH:MM:SS',
          'HH:MM:SS',
          'HH:MM',
          'MM:SS',
          'hours',
          'minutes',
          'seconds', 
          'milliseconds'
        ];
        formats.value[index] = 'days HH:MM:SS';
      }
      // Date and time format (YYYY-MM-DD HH:MM:SS)
      else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) {
        formatOptions.value[index] = [
          'DD-MM-YYYY HH:MM:SS',
          'DD/MM/YYYY HH:MM:SS',
          'MM-DD-YYYY HH:MM:SS',
          'MM/DD/YYYY HH:MM:SS',
          'YYYY-MM-DD HH:MM:SS',
          'YYYY/MM/DD HH:MM:SS'
        ];
        formats.value[index] = 'YYYY-MM-DD HH:MM:SS';
      }
      else {
        formatOptions.value[index] = [
        'string',
        'uppercase',
        'lowercase',
        'capitalize'
        ];
        formats.value[index] = 'string';
      }
    }

    // Format options for number values.
    if (valueType === 'number') {
      if (Number.isInteger(value)) {
        formatOptions.value[index] = ['integer', 'string'];
        formats.value[index] = 'integer';
      } else {
        formatOptions.value[index] = [
          '2 Decimal',
          '3 Decimal',
          '4 Decimal',
          '5 Decimal',
          '6 Decimal',
          '7 Decimal',
          'scientific'
        ];
        formats.value[index] = '2 Decimal';
      }
    }

    // Format options for boolean values.
    if (valueType === 'boolean') {
      formatOptions.value[index] = ['boolean', 'yes/no', 'true/false', '1/0'];
      formats.value[index] = 'boolean';
    }
    
  }

  const handleSubmit = async() => {
    loading.value = true;
    error.value = '';

    // Replace spaces with underscores in the name.
    const formattedName = name.value.replace(/\s+/g, '_').toLowerCase();

    const eventDetails = {
      name: formattedName,
      columns: columns.value,
      values: values.value,
      formats: formats.value,
      includeCoordinates: includeCoordinates.value
    }

    // Emit the new calculation details and close the modal.
    emit('reportDetails', eventDetails);
    emit('close');
  }

  const close = () => {
    emit('close');
  };

  const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);
</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex justify-center items-center z-50" @mousedown.self="onBackdropMousedown" @mouseup.self="onBackdropMouseup">
    <div class="ui-modal p-[20px] rounded-lg w-[600px] max-h-[calc(100vh-50px)] overflow-y-auto" @mousedown="onContentMousedown">
      <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
      <h2 class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">Create A New Report</h2>
      <p class="font-normal text-center text-orange-600">WARNING: This will affect all devices that share the same template.</p>
      <br>
      <form @submit.prevent="handleSubmit">

        <!-- Collection name -->
        <div class="mb-4">
          <label for="report-name" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Report Name</label>
          <input id="report-name" v-model="name" type="text" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" required />
        </div>

        <!-- columns -->
        <div v-for="(value, index) in columns" :key="index" class="mt-5 flex gap-2">
          <div v-if="columns.length > 1" class="flex gap-2 items-center hover:cursor-pointer pr-2 pt-5" @click="columns.splice(index, 1)">
            <TrashIcon class="h-[25px] w-[25px]"/>
          </div>

          <!-- Source -->
          <label class="font-bold">
              Column Header:
            <input v-model="columns[index]" type="text" class="w-full mt-1 p-1.5 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark font-normal focus-visible:outline-none focus-visible:border-mc-red" required />
          </label>

          <!-- field -->
          <label class="font-bold w-[175px]">
            Value:
            <select :value="values[index] || ''" @change="handleValueChange($event, index)" class="w-full mt-1 p-1.5 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark font-normal focus-visible:outline-none focus-visible:border-mc-red" required>
              <option value="blank">Leave Empty</option>
              <option v-for="value in columnOptions"
                :key="value"
                :value="value">
                {{ value }}
              </option>
            </select>
          </label>

          <!-- Format -->
          <label class="font-bold w-[175px]">
            Format:
            <select :value="formats[index] || ''" @change="formats[index] = $event.target.value" :disabled="!values[index] || values[index] === 'blank'" class="w-full mt-1 p-1.5 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark font-normal focus-visible:outline-none focus-visible:border-mc-red disabled:opacity-50" required>
              <option v-for="option in formatOptions[index]" :key="option" :value="option">
                {{ option }}
              </option>
            </select>
          </label>

          <!-- Add to operation -->
          <button type="button"
            v-if="value.source && value.field"
            @click="source = `${value.source}.${value.field}`"
            class="default-button w-[70px] h-[40px] text-center p-0 mt-6">
            Insert in operation
          </button>
        </div>

        <!-- Add another variable -->
        <button type="button" @click="columns.push('')" class="text-mesh-brand-blue font-bold" :class="{'ml-10': columns.length > 1}">+ Add another value</button>
        <br>
        <br>
        <!-- Include location data? -->
        <label class="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted cursor-pointer">
          <input v-model="includeCoordinates" type="checkbox" class="mr-2" />
          Include Coordinates
        </label>

        <!-- Error -->
        <div v-if="error" class="mt-5">
          <p class="font-normal ml-2 text-red-600 whitespace-pre-line">* {{ error }}</p>
        </div>

        <!-- Save and close -->
        <div class="flex justify-between mt-10">
          <button type="button" @click="close" class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160">Cancel</button>
          <LoadingAnimation3 v-if="loading" size="30px"></LoadingAnimation3>
          <button v-else type="submit" class="default-button" :class="{'default-button-off': loading}">Create</button>
        </div>
      </form>
    </div>
    <div id="preview">

    </div>
  </div>
</template>
